/**
 * quantity-offers.js
 * ---------------------------------------------------------------------------
 * Local replacement for the three the previous vendor PDP custom elements that used to be
 * supplied by the vendor runtime :
 *
 *   <quantity-breaks>         snippets/quantity-breaks.liquid:38 … :604
 *   <quantity-break-upsells>  snippets/quantity-breaks.liquid:414 … :594
 *   <quantity-gifts>          snippets/quantity-gifts.liquid:25 … :130
 *
 * These three have no Dawn upstream, so every selector, class, attribute and
 * every piece of price arithmetic below was derived by reading the Liquid that
 * renders them. File + line references are quoted at each use site.
 *
 * DESIGN NOTES
 * ---------------------------------------------------------------------------
 * 1. Everything lives inside one IIFE. The only globals created are the three
 *    customElements.define() registrations. Nothing here shadows a helper from
 *    assets/secondary.js, and nothing here defines `debounce`, `trapFocus`,
 *    `fetchConfig`, `publish`, `subscribe` or `PUB_SUB_EVENTS` — those belong
 *    to the shared core (Dawn global.js equivalent). Where this file needs an
 *    equivalent it uses a private `qo`-prefixed copy that cannot collide.
 *
 * 2. All lookups are instance-relative or data-attribute based. assets/quick-add.js
 *    (preventDuplicatedIDs / fixQuantityBreaks) rewrites the section id to
 *    `quickadd-<id>` and appends '-QuickAdd' to every input id and label[for]
 *    inside a cloned <quantity-breaks>, so id-based pairing is never used:
 *    input[data-input="input_N"] <-> label[data-input="input_N"]
 *    (quantity-breaks.liquid:186 and :192).
 *
 * 3. Cart writes for gifts/upsells run through ONE engine that evaluates every
 *    managed element against a SINGLE /cart.js snapshot and then issues at most
 *    two requests. This is deliberately the same philosophy that the store's own
 *    snippets/free-gifts.liquid documents in its header (lines 12-19): two
 *    independent engines each reconciling their own line cannot stay correct.
 *
 * 4. Every listener body is wrapped so a failure in the offers UI can never
 *    break add-to-cart.
 */
(function () {
  'use strict';

  /* =======================================================================
   * Private utilities
   * ===================================================================== */

  /** Run fn, swallow and log any error. Never let an offers bug break the PDP. */
  function qoTry(fn, label) {
    try {
      return fn();
    } catch (error) {
      if (window.console && console.warn) {
        console.warn('[quantity-offers]' + (label ? ' ' + label : ''), error);
      }
      return undefined;
    }
  }

  /** Local debounce. The global `debounce` belongs to the shared core file. */
  function qoDebounce(fn, wait) {
    var timer = null;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  function qoArray(nodeList) {
    return Array.prototype.slice.call(nodeList || []);
  }

  /** window.routes is defined at layout/theme.liquid:672-679. Never hardcode. */
  function qoRoute(key, fallback) {
    return (window.routes && window.routes[key]) || fallback;
  }

  /* ---------------------------------------------------------------------
   * Money
   * -------------------------------------------------------------------
   * data-money-format is `shop.money_format | escape` (quantity-breaks.liquid:48).
   * snippets/text-with-price.liquid:1-6 pipes every money token through
   * `money_without_trailing_zeros`, so the ".00" has to be dropped the same way.
   * The token table mirrors Shopify's own money_format placeholders; the local
   * conventions match snippets/free-gifts.liquid:965-981, which already does
   * this job for the gift progress bar.
   * ------------------------------------------------------------------- */

  var QO_MONEY_TOKENS = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_with_space_separator: [2, ' ', ','],
    amount_no_decimals_with_space_separator: [0, ' ', '.'],
    amount_with_period_and_space_separator: [2, ' ', '.']
  };

  function qoMoneyDigits(cents, precision, thousands, decimal) {
    var fixed = (cents / 100).toFixed(precision).split('.');
    var whole = fixed[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (fixed[1] ? decimal + fixed[1] : '');
  }

  /**
   * @param {number} cents
   * @param {string} format  shop.money_format, e.g. "${{amount}}"
   * @param {boolean} withoutTrailingZeros  emulate `money_without_trailing_zeros`
   */
  function qoFormatMoney(cents, format, withoutTrailingZeros) {
    var amount = Math.round(Number(cents));
    if (!isFinite(amount)) amount = 0;

    var match = /\{\{\s*(\w+)\s*\}\}/.exec(format || '');
    var spec = QO_MONEY_TOKENS[match ? match[1] : 'amount'] || QO_MONEY_TOKENS.amount;
    var precision = spec[0];

    // money_without_trailing_zeros only drops the decimals when there are none.
    if (withoutTrailingZeros && amount % 100 === 0) precision = 0;

    var value = qoMoneyDigits(amount, precision, spec[1], spec[2]);
    return format ? format.replace(/\{\{\s*\w+\s*\}\}/, value) : value;
  }

  /**
   * Reproduce the token substitution of snippets/text-with-price.liquid:8-17.
   * `name` is never passed by quantity-breaks.liquid, so Liquid replaces
   * [name] with nil, i.e. an empty string.
   */
  function qoRenderPriceText(template, ctx, moneyFormat) {
    var money = function (value) {
      return qoFormatMoney(value, moneyFormat, true);
    };
    var pairs = [
      ['[quantity]', String(ctx.quantity)],
      ['[name]', ''],
      ['[price]', money(ctx.price)],
      ['[compare_price]', money(ctx.comparePrice)],
      ['[amount_saved]', money(ctx.amountSaved)],
      ['[amount_saved_rounded]', money(ctx.amountSavedRounded)],
      ['[price_each]', money(ctx.priceEach)],
      ['[compare_price_each]', money(ctx.comparePriceEach)]
    ];
    var out = String(template == null ? '' : template);
    for (var i = 0; i < pairs.length; i++) {
      out = out.split(pairs[i][0]).join(pairs[i][1]);
    }
    return out;
  }

  /** Add/replace a `width=` param on a CDN image url (Liquid `image_url: width: N`). */
  function qoImageUrl(src, width) {
    if (!src) return src;
    var parts = String(src).split('?');
    var params = (parts[1] || '').split('&').filter(function (p) {
      return p && p.indexOf('width=') !== 0;
    });
    params.push('width=' + width);
    return parts[0] + '?' + params.join('&');
  }

  /**
   * 'products.product.value_unavailable' | t (quantity-breaks.liquid:385) is
   * exposed client-side as window.variantStrings.unavailable_with_option with a
   * '[value]' placeholder (layout/theme.liquid:690). Never hand-roll the string.
   */
  function qoUnavailableLabel(value) {
    var template = window.variantStrings && window.variantStrings.unavailable_with_option;
    return template ? String(template).replace('[value]', value) : value;
  }

  /* ---------------------------------------------------------------------
   * localStorage (shared by <quantity-break-upsells> and, in future, by
   * <cart-drawer-gift> — the three elements that carry the attribute pair
   * data-main-product + data-save-local-storage="true":
   *   quantity-breaks.liquid:418-419, quantity-gifts.liquid:29-30,
   *   cart-gift.liquid:64-66).
   * No key convention existed anywhere in the theme, so one is invented here.
   * ------------------------------------------------------------------- */

  var QO_STORAGE_PREFIX = 'quantity-offers:v1:';

  function qoRead(handle) {
    return qoTry(function () {
      var raw = window.localStorage.getItem(QO_STORAGE_PREFIX + handle);
      var parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    }, 'storage read') || {};
  }

  function qoWrite(handle, data) {
    qoTry(function () {
      window.localStorage.setItem(QO_STORAGE_PREFIX + handle, JSON.stringify(data));
    }, 'storage write');
  }

  /* =======================================================================
   * Cart engine
   * ---------------------------------------------------------------------
   * Owns every cart write made on behalf of <quantity-gifts> and
   * <quantity-break-upsells>. One /cart.js snapshot in, at most two writes out.
   *
   * A gift or upsell is only ever put in the cart once the MAIN product is
   * actually in the cart at the required quantity. The quantity_gifts schema
   * paragraph (sections/main-product.liquid:8168) says the gifts are
   * "automatically added & removed from cart, based on the quantity of the main
   * product page product", and reconciling against the cart is the only reading
   * of that which cannot strand a gift in an otherwise empty cart.
   * ===================================================================== */

  var QuantityOffersEngine = {
    cart: null,
    busy: false,
    queued: false,
    suppressObserver: false,

    /** Every managed line on the page, from both element types. */
    entries: function () {
      var out = [];

      // <quantity-gifts> -> div.quantity-gift[data-product][data-quantity]
      // quantity-gifts.liquid:73-78
      qoArray(document.querySelectorAll('quantity-gifts')).forEach(function (host) {
        var handle = host.getAttribute('data-main-product') || '';
        qoArray(host.querySelectorAll('.quantity-gift')).forEach(function (el) {
          var id = parseInt(el.getAttribute('data-product'), 10);
          if (!id) return;
          out.push({
            el: el,
            kind: 'gift',
            id: id,
            mainHandle: handle,
            threshold: parseInt(el.getAttribute('data-quantity'), 10) || 1,
            wanted: null
          });
        });
      });

      // <quantity-break-upsells> -> div.quantity-break__upsell
      // quantity-breaks.liquid:536-543
      qoArray(document.querySelectorAll('quantity-break-upsells')).forEach(function (host) {
        var handle = host.getAttribute('data-main-product') || '';
        qoArray(host.querySelectorAll('.quantity-break__upsell')).forEach(function (el) {
          var id = parseInt(el.getAttribute('data-product'), 10);
          if (!id) return;
          out.push({
            el: el,
            kind: 'upsell',
            id: id,
            mainHandle: handle,
            // data-min-quantity is the option_N_quantity of the offer named by
            // data-min-offer (quantity-breaks.liquid:479-490, :539).
            threshold: parseInt(el.getAttribute('data-min-quantity'), 10) || 1,
            // data-toggle="false" is a gift: auto-added, no user control
            // ("Gift (auto-added)", sections/main-product.liquid:2983).
            selected: el.getAttribute('data-toggle') !== 'true' || el.getAttribute('data-selected') === 'true'
          });
        });
      });

      return out;
    },

    /** Units of `handle` currently in the cart, ignoring free-gifts.liquid's own lines. */
    cartQuantityOf: function (handle) {
      if (!this.cart || !this.cart.items || !handle) return 0;
      var total = 0;
      this.cart.items.forEach(function (item) {
        if (item.handle !== handle) return;
        if (item.properties && item.properties._free_gift) return;
        total += item.quantity || 0;
      });
      return total;
    },

    cartLineFor: function (variantId) {
      if (!this.cart || !this.cart.items) return null;
      for (var i = 0; i < this.cart.items.length; i++) {
        var item = this.cart.items[i];
        // free-gifts.liquid adds its lines with a hidden `_free_gift` property
        // (its header comment, lines 51-55). Those belong to the other engine.
        if (item.properties && item.properties._free_gift) continue;
        if (item.id === variantId || item.variant_id === variantId) return item;
      }
      return null;
    },

    fetchCart: function () {
      var self = this;
      return fetch(qoRoute('cart_url', '/cart') + '.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          self.cart = cart;
          return cart;
        });
    },

    /** Section ids to ask the Section Rendering API for, based on what is on the page. */
    sectionsToRender: function () {
      var ids = [];
      if (document.querySelector('cart-drawer')) ids.push('cart-drawer');

      // sections/main-cart-items.liquid:44 and main-cart-footer.liquid:1 carry
      // the real section id in data-id.
      var items = document.getElementById('main-cart-items');
      if (items) ids.push(items.getAttribute('data-id') || 'main-cart-items');
      var footer = document.getElementById('main-cart-footer');
      if (footer) ids.push(footer.getAttribute('data-id') || 'main-cart-footer');

      // sections/header.liquid:767
      if (document.getElementById('cart-icon-bubble')) ids.push('cart-icon-bubble');

      return ids;
    },

    /**
     * Apply re-rendered sections. The selector list is deliberately the same one
     * snippets/free-gifts.liquid:653 uses, so both engines leave the drawer in
     * the same shape.
     */
    applySections: function (payload) {
      var sections = payload && payload.sections;
      if (!sections) return;
      var self = this;

      self.suppressObserver = true;

      qoTry(function () {
        Object.keys(sections).forEach(function (key) {
          var html = sections[key];
          if (typeof html !== 'string' || !html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');

          // Cart count bubble: the section wraps the bubble markup, so copy the
          // section body into the live <a id="cart-icon-bubble">.
          if (key === 'cart-icon-bubble') {
            var bubble = document.getElementById('cart-icon-bubble');
            var fresh = doc.querySelector('.shopify-section') || doc.body;
            if (bubble && fresh) bubble.innerHTML = fresh.innerHTML;
            return;
          }

          ['#CartDrawer-CartItems', '.drawer__footer', '.drawer__header', '#main-cart-items', '#main-cart-footer'].forEach(
            function (selector) {
              var source = doc.querySelector(selector);
              var live = document.querySelector(selector);
              if (source && live) live.innerHTML = source.innerHTML;
            }
          );

          // Keep `is-empty` on <cart-drawer> honest (snippets/cart-drawer.liquid:29).
          var freshDrawer = doc.querySelector('cart-drawer');
          var liveDrawer = document.querySelector('cart-drawer');
          if (freshDrawer && liveDrawer) {
            liveDrawer.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
          }
        });
      }, 'applySections');

      setTimeout(function () {
        self.suppressObserver = false;
      }, 0);
    },

    post: function (url, body) {
      var sections = this.sectionsToRender();
      if (sections.length) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body)
      }).then(function (response) {
        return response.json();
      });
    },

    /**
     * Evaluate every managed element against one cart snapshot, then batch the
     * result into at most two writes:
     *   /cart/update.js  (removals + quantity corrections, keyed by variant id)
     *   /cart/add.js     (all missing lines in a single items[] payload)
     */
    reconcile: function () {
      var self = this;

      if (self.busy) {
        self.queued = true;
        return Promise.resolve();
      }

      var entries = self.entries();
      if (!entries.length) return Promise.resolve();

      self.busy = true;

      return self
        .fetchCart()
        .then(function () {
          // Refresh the unlocked/locked visuals from the same snapshot.
          self.refreshVisuals();

          var wanted = {}; // variantId -> true
          var managed = {}; // variantId -> true

          entries.forEach(function (entry) {
            managed[entry.id] = true;
            var cartQty = self.cartQuantityOf(entry.mainHandle);
            var live = cartQty > 0 && cartQty >= entry.threshold;
            var want = live && (entry.kind === 'gift' ? true : entry.selected);
            if (want) wanted[entry.id] = true;
          });

          var updates = {};
          var adds = [];
          var changed = false;

          Object.keys(managed).forEach(function (key) {
            var variantId = parseInt(key, 10);
            var line = self.cartLineFor(variantId);
            if (wanted[variantId]) {
              if (!line) {
                adds.push({ id: variantId, quantity: 1 });
                changed = true;
              } else if (line.quantity !== 1) {
                updates[variantId] = 1;
                changed = true;
              }
            } else if (line) {
              updates[variantId] = 0;
              changed = true;
            }
          });

          if (!changed) return null;

          var chain = Promise.resolve(null);

          if (Object.keys(updates).length) {
            chain = chain.then(function () {
              return self.post(qoRoute('cart_update_url', '/cart/update'), { updates: updates });
            });
          }

          if (adds.length) {
            chain = chain.then(function (previous) {
              return self.post(qoRoute('cart_add_url', '/cart/add'), { items: adds }).catch(function (error) {
                // A sold-out or untracked gift can legitimately fail to add.
                // Never let that reject the whole reconcile.
                if (window.console && console.warn) console.warn('[quantity-offers] add failed', error);
                return previous;
              });
            });
          }

          return chain.then(function (last) {
            if (last) self.applySections(last);
            return self.fetchCart().then(function () {
              self.refreshVisuals();
              return last;
            });
          });
        })
        .catch(function (error) {
          if (window.console && console.warn) console.warn('[quantity-offers] reconcile failed', error);
        })
        .then(function () {
          self.busy = false;
          if (self.queued) {
            self.queued = false;
            self.scheduleReconcile();
          }
        });
    },

    /** Ask every <quantity-gifts> on the page to recompute its lock state. */
    refreshVisuals: function () {
      qoArray(document.querySelectorAll('quantity-gifts')).forEach(function (host) {
        if (typeof host.evaluate === 'function') qoTry(host.evaluate.bind(host), 'gift evaluate');
      });
    },

    scheduleReconcile: null, // assigned below (debounced)
    scheduleVisuals: null,

    started: false,

    start: function () {
      if (this.started) return;
      this.started = true;

      var self = this;

      // snippets/free-gifts.liquid:660 dispatches this after every cart write it
      // observes (it patches window.fetch and XMLHttpRequest, see its header
      // comment lines 22-28), so it is the most reliable cart signal in the theme.
      document.addEventListener('cart:updated', function () {
        self.scheduleReconcile();
      });

      // Dawn's pub/sub lives in the shared core file; consume it only if present.
      qoTry(function () {
        if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
          window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () {
            self.scheduleReconcile();
          });
        }
      }, 'subscribe cartUpdate');

      // Fallback signal that needs no fetch patching: every cart write in this
      // theme re-renders <a id="cart-icon-bubble"> (sections/header.liquid:767).
      qoTry(function () {
        var bubble = document.getElementById('cart-icon-bubble');
        if (!bubble || typeof MutationObserver !== 'function') return;
        var observer = new MutationObserver(function () {
          if (self.suppressObserver) return;
          self.scheduleReconcile();
        });
        observer.observe(bubble, { childList: true, subtree: true, characterData: true });
      }, 'bubble observer');

      // Any quantity change on the page (offer radio, quantity-input +/-)
      // only affects the PDP visuals, never the cart.
      document.addEventListener('change', function () {
        self.scheduleVisuals();
      });

      this.scheduleReconcile();
    }
  };

  QuantityOffersEngine.scheduleReconcile = qoDebounce(function () {
    QuantityOffersEngine.reconcile();
  }, 250);

  QuantityOffersEngine.scheduleVisuals = qoDebounce(function () {
    QuantityOffersEngine.refreshVisuals();
  }, 30);

  /* =======================================================================
   * <quantity-breaks>   snippets/quantity-breaks.liquid:38 … :604
   * ===================================================================== */

  var QuantityBreaks = class extends HTMLElement {
    connectedCallback() {
      if (this.qoReady) return;
      this.qoReady = true;
      qoTry(this.qoInit.bind(this), 'quantity-breaks init');
    }

    qoInit() {
      this.sectionId = this.getAttribute('data-section') || '';

      // div.quantity-breaks-container — quantity-breaks.liquid:65
      this.container = this.querySelector('.quantity-breaks-container');
      if (!this.container) return;

      this.moneyFormat = this.getAttribute('data-money-format') || '';
      this.shouldUpdatePrices = this.getAttribute('data-update-prices') === 'true'; // :46
      this.shouldUpdatePdpPrices = this.getAttribute('data-update-pdp-prices') === 'true'; // :47
      this.shouldUpdateUnavailable = this.getAttribute('data-update-unavailable') === 'true'; // :43-45
      this.skipNonExistent = this.getAttribute('data-skip-non-existent') === 'true'; // :50
      this.skipUnavailable = this.getAttribute('data-skip-unavailable') === 'true'; // :51
      this.preventGalleryScroll = this.hasAttribute('data-prevent-gallery-scroll'); // :52-54

      this.basePrice = parseFloat(this.getAttribute('data-base-price')) || 0; // :49
      this.variantData = this.qoVariantData();
      this.currentVariant = this.qoResolveCurrentVariant();

      // The main product form. product_form_id = 'product-form-' | append: section.id
      // (sections/main-product.liquid:119), used by {% form 'product' %} in
      // snippets/buy-buttons.liquid:80-86.
      this.productForm = document.getElementById('product-form-' + this.sectionId);
      // div.hidden.product-form__variants — snippets/buy-buttons.liquid:87
      this.variantsHolder = document.getElementById('product-form-' + this.sectionId + '__variants');

      this.qoBindEvents();

      // Initial pass: seed availability, prices and the multi-variant hand-off.
      this.qoRenderOfferPrices();
      this.qoRefreshAllSelectorAvailability();
      this.qoSyncSelection();
    }

    /* ---------------- data ---------------- */

    /**
     * script[type="application/json"] holding {{ product.variants | json }} —
     * quantity-breaks.liquid:601-603 (direct child, after the container).
     */
    qoVariantData() {
      var script = this.querySelector('script[type="application/json"]');
      if (!script) return [];
      var parsed = qoTry(function () {
        return JSON.parse(script.textContent);
      }, 'variant json');
      return Array.isArray(parsed) ? parsed : [];
    }

    qoVariantById(id) {
      var wanted = parseInt(id, 10);
      for (var i = 0; i < this.variantData.length; i++) {
        if (this.variantData[i].id === wanted) return this.variantData[i];
      }
      return null;
    }

    qoVariantByOptions(values) {
      for (var i = 0; i < this.variantData.length; i++) {
        var variant = this.variantData[i];
        var options = variant.options || [];
        var match = options.length === values.length;
        for (var j = 0; match && j < values.length; j++) {
          if (options[j] !== values[j]) match = false;
        }
        if (match) return variant;
      }
      return null;
    }

    /**
     * The base variant is whatever the main form currently points at:
     * input.product-variant-id[name="id"] — snippets/buy-buttons.liquid:88-96.
     */
    qoResolveCurrentVariant() {
      var input = this.productFormInput();
      var variant = input ? this.qoVariantById(input.value) : null;
      if (variant) return variant;
      // Fall back to the render-time price so recomputation still works.
      return null;
    }

    productFormInput() {
      var form = document.getElementById('product-form-' + this.sectionId);
      return form ? form.querySelector('input[name="id"]') : null;
    }

    /* ---------------- events ---------------- */

    qoBindEvents() {
      var self = this;

      // One delegated listener: the offer radios and the per-unit selects both
      // live inside .quantity-breaks-container.
      this.container.addEventListener('change', function (event) {
        qoTry(function () {
          var target = event.target;
          if (!target) return;

          if (target.matches('input[type="radio"][name="quantity"]')) {
            self.qoSyncSelection();
            return;
          }
          // select.quantity-break__variant-select — quantity-breaks.liquid:326-330
          if (target.matches('select.quantity-break__variant-select')) {
            self.qoOnSelectorChange(target);
          }
        }, 'quantity-breaks change');
      });

      // Main variant change. Dawn's VariantSelects sets the hidden id input and
      // dispatches a bubbling 'change' on it, so listening on the <form> catches
      // it without inventing an event name.
      if (this.productForm) {
        this.productForm.addEventListener('change', function (event) {
          if (!event.target || event.target.name !== 'id') return;
          qoTry(self.qoOnMainVariantChange.bind(self), 'main variant change');
        });
      }

      // If the shared core exposes Dawn's pub/sub, use it too (idempotent).
      qoTry(function () {
        if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) {
          window.subscribe(window.PUB_SUB_EVENTS.variantChange, function (payload) {
            var data = payload && payload.data;
            if (data && data.sectionId && data.sectionId !== self.sectionId) return;
            qoTry(self.qoOnMainVariantChange.bind(self), 'main variant change (pubsub)');
          });
        }
      }, 'subscribe variantChange');
    }

    qoOnMainVariantChange() {
      this.currentVariant = this.qoResolveCurrentVariant();
      if (this.currentVariant && typeof this.currentVariant.price === 'number') {
        this.basePrice = this.currentVariant.price;
        this.setAttribute('data-base-price', String(this.currentVariant.price));
      }
      this.qoRenderOfferPrices();
      this.qoUpdatePdpPrices();
    }

    /* ---------------- selection ---------------- */

    /**
     * label[data-input] pairs with input[data-input] (quantity-breaks.liquid:186,
     * :192). Never use ids: quick-add.js rewrites them.
     */
    qoSelectedRadio() {
      return this.container.querySelector('input[type="radio"][name="quantity"]:checked');
    }

    qoLabelFor(radio) {
      if (!radio) return null;
      var key = radio.getAttribute('data-input');
      if (key) {
        var label = this.container.querySelector('label.quantity-break[data-input="' + key + '"]');
        if (label) return label;
      }
      var next = radio.nextElementSibling;
      return next && next.classList.contains('quantity-break') ? next : null;
    }

    qoSelectedLabel() {
      return this.qoLabelFor(this.qoSelectedRadio());
    }

    /** Called after any selection change. */
    qoSyncSelection() {
      this.qoWriteVariantValues();
      this.qoUpdatePdpPrices();

      // Let <quantity-gifts> in the same section react to the picked quantity.
      var radio = this.qoSelectedRadio();
      var detail = {
        sectionId: this.sectionId,
        quantity: radio ? parseInt(radio.value, 10) || 0 : 0
      };
      qoTry(function () {
        document.dispatchEvent(new CustomEvent('quantity-offers:quantity', { detail: detail }));
      }, 'dispatch quantity');
    }

    /* ---------------- prices ---------------- */

    /**
     * Reproduce quantity-breaks.liquid:157-172 exactly, using each label's own
     * data-percentage-left (:194), data-fixed-discount (:195) and
     * data-compare-mode (:196) rather than re-deriving them from settings.
     */
    qoOfferMath(label) {
      var qty = parseInt(label.getAttribute('data-quantity'), 10) || 1;
      var percentageLeft = parseFloat(label.getAttribute('data-percentage-left'));
      if (!isFinite(percentageLeft)) percentageLeft = 1;
      var fixedDiscount = parseFloat(label.getAttribute('data-fixed-discount')) || 0;
      var compareMode = label.getAttribute('data-compare-mode');

      var basePrice = this.basePrice;
      var price = basePrice * qty * percentageLeft - fixedDiscount; // :162

      var comparePrice = basePrice; // :164
      var variantCompare = this.currentVariant ? this.currentVariant.compare_at_price : null;
      if (variantCompare == null) {
        // No live variant yet — keep the render-time per-unit compare price (:193).
        var rendered = parseFloat(label.getAttribute('data-base-compare-price'));
        if (isFinite(rendered) && rendered > basePrice && compareMode === 'compare_price') {
          comparePrice = rendered;
        }
      } else if (compareMode === 'compare_price' && variantCompare > basePrice) {
        comparePrice = variantCompare; // :165-167
      }

      var compareTotal = comparePrice * qty; // :168
      var amountSaved = compareTotal - price; // :169
      var amountSavedRounded = Math.round(amountSaved / 100) * 100; // :170

      return {
        quantity: qty,
        price: price,
        comparePrice: compareTotal,
        amountSaved: amountSaved,
        amountSavedRounded: amountSavedRounded,
        priceEach: price / qty, // :171
        comparePriceEach: compareTotal / qty, // :172
        basePerUnitCompare: comparePrice
      };
    }

    qoRenderOfferPrices() {
      if (!this.shouldUpdatePrices) return;
      var self = this;

      qoArray(this.container.querySelectorAll('label.quantity-break')).forEach(function (label) {
        var ctx = self.qoOfferMath(label);

        // Keep the per-unit compare price attribute in step (:193).
        label.setAttribute('data-base-compare-price', String(ctx.basePerUnitCompare));

        // Every .variant-price-update carries its own inline_richtext template in
        // data-text (:200, :231, :245, :261, :278, :292). Richtext -> innerHTML.
        qoArray(label.querySelectorAll('.variant-price-update')).forEach(function (node) {
          var template = node.getAttribute('data-text');
          if (template == null) return;
          node.innerHTML = qoRenderPriceText(template, ctx, self.moneyFormat);
        });

        // span.quantity-break__compare-price gets `hidden` when
        // compare_total <= price (:292).
        var compare = label.querySelector('.quantity-break__compare-price');
        if (compare) compare.classList.toggle('hidden', ctx.comparePrice <= ctx.price);
      });
    }

    /**
     * data-update-pdp-prices (:47) — "updates the displayed price in the Price
     * block, Sticky Add to Cart block & add to cart button"
     * (sections/main-product.liquid:2650).
     *
     * Only section-scoped targets are touched:
     *   span.main-atc-price-span-<section.id>  buy-buttons.liquid:130 / :146
     *                                          and main-product.liquid:657
     *   .price-<section.id> .main-price        price.liquid:59, :77 / :85
     *   .price-<section.id> .main-comapre-price [sic] price.liquid:59, :93
     */
    qoUpdatePdpPrices() {
      if (!this.shouldUpdatePdpPrices) return;
      var label = this.qoSelectedLabel();
      if (!label) return;

      var ctx = this.qoOfferMath(label);
      var format = this.moneyFormat;
      var sectionId = this.sectionId;

      qoTry(function () {
        // These spans are rendered with `| money`, not money_without_trailing_zeros.
        var priceText = qoFormatMoney(ctx.price, format, false);
        var compareText = qoFormatMoney(ctx.comparePrice, format, false);

        qoArray(document.querySelectorAll('.main-atc-price-span-' + sectionId)).forEach(function (node) {
          node.innerHTML = priceText;
        });

        var priceBlock = document.querySelector('.price-' + sectionId);
        if (priceBlock) {
          qoArray(priceBlock.querySelectorAll('.main-price')).forEach(function (node) {
            node.innerHTML = priceText;
          });
          if (ctx.comparePrice > ctx.price) {
            qoArray(priceBlock.querySelectorAll('.main-comapre-price')).forEach(function (node) {
              node.innerHTML = compareText;
            });
          }
        }
      }, 'pdp prices');
    }

    /* ---------------- per-unit variant pickers ---------------- */

    qoSelectorItems(label) {
      // div.quantity-break__selector-item — quantity-breaks.liquid:318-322
      return qoArray(label.querySelectorAll('.quantity-break__selector-item'));
    }

    qoItemSelects(item) {
      return qoArray(item.querySelectorAll('select.quantity-break__variant-select'));
    }

    qoOnSelectorChange(select) {
      var item = select.closest('.quantity-break__selector-item');
      if (!item) return;

      var values = this.qoItemSelects(item).map(function (node) {
        return node.value;
      });
      var variant = this.qoVariantByOptions(values);

      if (variant) {
        // :321 data-selected-id and :329 data-product-id, kept in step.
        item.setAttribute('data-selected-id', String(variant.id));
        this.qoItemSelects(item).forEach(function (node) {
          node.setAttribute('data-product-id', String(variant.id));
        });

        // div.quantity-break__selector-item__image > img — :396-404
        var img = item.querySelector('.quantity-break__selector-item__image img');
        if (img && variant.featured_image && variant.featured_image.src) {
          img.setAttribute('src', qoImageUrl(variant.featured_image.src, 600));
        }

        this.qoScrollGallery(variant);
      }

      this.qoUpdateSelectorAvailability(item);
      this.qoWriteVariantValues();
      this.qoRenderOfferPrices();
    }

    /**
     * media-gallery#MediaGallery-<section.id>[data-section] —
     * snippets/product-media-gallery.liquid:85-88. Media ids are prefixed with
     * the section id (product-media-gallery.liquid:123, :161, :307, :357).
     * data-prevent-gallery-scroll (:52-54) means "skip this call".
     */
    qoScrollGallery(variant) {
      if (this.preventGalleryScroll) return;
      if (!variant || !variant.featured_media) return;
      var gallery = document.querySelector('media-gallery[data-section="' + this.sectionId + '"]');
      if (!gallery || typeof gallery.setActiveMedia !== 'function') return;
      qoTry(function () {
        gallery.setActiveMedia(this.sectionId + '-' + variant.featured_media.id, true);
      }.bind(this), 'gallery scroll');
    }

    qoRefreshAllSelectorAvailability() {
      var self = this;
      qoArray(this.container.querySelectorAll('.quantity-break__selector-item')).forEach(function (item) {
        self.qoUpdateSelectorAvailability(item);
      });
    }

    /**
     * Cascading availability, mirroring quantity-breaks.liquid:332-372.
     * Option N is tested against the values already chosen for options 1..N-1
     * inside THIS selector item (the Liquid anchors on the main variant only
     * because no per-unit state exists at render time).
     */
    qoUpdateSelectorAvailability(item) {
      if (!this.shouldUpdateUnavailable) return;

      var self = this;
      var selects = this.qoItemSelects(item);
      var chosen = selects.map(function (node) {
        return node.value;
      });

      selects.forEach(function (select, index) {
        qoArray(select.options).forEach(function (option) {
          var combo = chosen.slice(0, index).concat([option.value]);
          var exists = false;
          var available = false;

          for (var i = 0; i < self.variantData.length; i++) {
            var variantOptions = self.variantData[i].options || [];
            var matches = true;
            for (var j = 0; j < combo.length; j++) {
              if (variantOptions[j] !== combo[j]) {
                matches = false;
                break;
              }
            }
            if (!matches) continue;
            exists = true;
            if (self.variantData[i].available) {
              available = true;
              break;
            }
          }

          // class is exactly '' | 'non-existent' | 'unavailable' (:365, :368) —
          // the same vocabulary as product-variant-options.liquid:81/:84.
          option.classList.remove('non-existent', 'unavailable');
          var hide = false;
          if (!exists) {
            option.classList.add('non-existent');
            hide = self.skipNonExistent;
          } else if (!available) {
            option.classList.add('unavailable');
            hide = self.skipUnavailable;
          }

          option.disabled = hide;
          if (hide) {
            option.setAttribute('hidden', 'hidden'); // :380-382
          } else {
            option.removeAttribute('hidden');
          }

          // :384-388 — the translated label is used whenever the option is
          // not buyable (non-existent counts as disabled in the Liquid).
          option.textContent = exists && available ? option.value : qoUnavailableLabel(option.value);
        });
      });
    }

    /**
     * Publish the per-unit variant ids so one add-to-cart can carry several
     * distinct variants. The container is div.hidden.product-form__variants
     * #product-form-<section.id>__variants[data-values] (buy-buttons.liquid:87).
     *
     * NOTE: the payload SHAPE is owned by the product-form cluster; no Liquid
     * file specifies it. A JSON array of variant ids in unit order is written
     * here, and the container is left empty of inputs so the form's normal
     * `id` + `quantity` submission can never be corrupted by this element.
     */
    qoWriteVariantValues() {
      if (!this.variantsHolder) return;
      var label = this.qoSelectedLabel();
      var ids = [];

      if (label) {
        this.qoSelectorItems(label).forEach(function (item) {
          var id = parseInt(item.getAttribute('data-selected-id'), 10);
          if (id) ids.push(id);
        });
      }

      this.variantsHolder.setAttribute('data-values', ids.length ? JSON.stringify(ids) : '');
    }
  };

  /* =======================================================================
   * <quantity-break-upsells>   snippets/quantity-breaks.liquid:414 … :594
   * ---------------------------------------------------------------------
   * One instance per offer. Owns up to three div.quantity-break__upsell
   * children (:536). All the collapsing/expanding is CSS
   * (base.css `.quantity-breaks-container input:not(:checked) + .quantity-break
   * .quantity-break__upsell--hide-inactive {height:0;...}`), and the checkbox
   * icon swap is CSS too (`[data-selected="false"] .checkmark-checked,
   * [data-selected="true"] .checkmark-unchecked {display:none}`), so the only
   * job here is flipping data-selected, persisting it, and telling the engine.
   * ===================================================================== */

  var QuantityBreakUpsells = class extends HTMLElement {
    connectedCallback() {
      if (this.qoReady) return;
      this.qoReady = true;
      qoTry(this.qoInit.bind(this), 'quantity-break-upsells init');
    }

    qoInit() {
      this.mainProduct = this.getAttribute('data-main-product') || ''; // :418
      this.saveState = this.getAttribute('data-save-local-storage') === 'true'; // :419
      this.breaks = this.closest('quantity-breaks');

      this.qoRestore();

      var self = this;
      this.addEventListener('click', function (event) {
        qoTry(function () {
          self.qoOnClick(event);
        }, 'upsell click');
      });

      QuantityOffersEngine.scheduleReconcile();
    }

    qoUpsells() {
      return qoArray(this.querySelectorAll('.quantity-break__upsell'));
    }

    /**
     * The whole subtree sits inside <label for="quantity{i}-…">
     * (quantity-breaks.liquid:188 … :596), so a click here natively activates
     * the offer radio. Rule adopted: the FIRST click on an upsell inside a
     * non-selected offer selects the offer (native label behaviour, untouched);
     * once the offer is selected, clicks toggle the checkbox instead.
     * Gift-type upsells (data-toggle="false", :537) are never toggleable.
     */
    qoOnClick(event) {
      var upsell = event.target && event.target.closest ? event.target.closest('.quantity-break__upsell') : null;
      if (!upsell || !this.contains(upsell)) return;
      if (upsell.getAttribute('data-toggle') !== 'true') return;

      var label = this.closest('label.quantity-break');
      if (label && this.breaks) {
        var key = label.getAttribute('data-input');
        var radio = key
          ? this.breaks.querySelector('.quantity-breaks-container input[data-input="' + key + '"]')
          : null;
        if (radio && !radio.checked) return; // let the label select the offer first
      }

      event.preventDefault();
      this.qoSetSelected(upsell, upsell.getAttribute('data-selected') !== 'true');
    }

    /**
     * The same upsell product can be rendered inside several offers
     * (the render gate at :492 is per-offer), so the state is mirrored to every
     * instance of that variant id within the owning <quantity-breaks>.
     */
    qoSetSelected(upsell, selected) {
      var value = selected ? 'true' : 'false';
      var variantId = upsell.getAttribute('data-product');
      var scope = this.breaks || this;

      qoArray(scope.querySelectorAll('.quantity-break__upsell[data-product="' + variantId + '"]')).forEach(function (node) {
        if (node.getAttribute('data-toggle') !== 'true') return;
        node.setAttribute('data-selected', value);
      });

      this.qoPersist();
      QuantityOffersEngine.scheduleReconcile();
    }

    qoStorageKey() {
      return this.mainProduct;
    }

    qoPersist() {
      if (!this.saveState || !this.mainProduct) return;
      var state = qoRead(this.qoStorageKey());
      state.upsells = state.upsells || {};
      var scope = this.breaks || this;
      qoArray(scope.querySelectorAll('.quantity-break__upsell[data-toggle="true"]')).forEach(function (node) {
        state.upsells[node.getAttribute('data-product')] = node.getAttribute('data-selected') === 'true';
      });
      qoWrite(this.qoStorageKey(), state);
    }

    qoRestore() {
      if (!this.saveState || !this.mainProduct) return;
      var state = qoRead(this.qoStorageKey());
      if (!state.upsells) return;
      this.qoUpsells().forEach(function (node) {
        if (node.getAttribute('data-toggle') !== 'true') return;
        var stored = state.upsells[node.getAttribute('data-product')];
        if (typeof stored === 'boolean') node.setAttribute('data-selected', stored ? 'true' : 'false');
      });
    }
  };

  /* =======================================================================
   * <quantity-gifts>   snippets/quantity-gifts.liquid:25 … :130
   * ---------------------------------------------------------------------
   * Visual unlocking only. `quantity-gift--unlocked` is the single JS-toggled
   * class (12 rules in base.css drive the lock fade, box opening, image reveal
   * and container recolour); data-unlocked (:76) is the idempotency flag so a
   * redundant recompute cannot restart the ~3s animation.
   * The cart side is handled by QuantityOffersEngine.
   * ===================================================================== */

  var QuantityGifts = class extends HTMLElement {
    connectedCallback() {
      if (this.qoReady) return;
      this.qoReady = true;
      qoTry(this.qoInit.bind(this), 'quantity-gifts init');
    }

    qoInit() {
      this.sectionId = this.getAttribute('data-section') || ''; // :28
      this.mainProduct = this.getAttribute('data-main-product') || ''; // :29

      // item_count == 0 renders an empty element plus a placeholder paragraph
      // outside it (:131-133). Nothing to do.
      if (!this.querySelector('.quantity-gift')) return;

      var self = this;
      this.qoOnQuantity = function (event) {
        var detail = event && event.detail;
        if (detail && detail.sectionId && detail.sectionId !== self.sectionId) return;
        qoTry(self.evaluate.bind(self), 'gift quantity event');
      };
      document.addEventListener('quantity-offers:quantity', this.qoOnQuantity);

      this.evaluate();
      QuantityOffersEngine.scheduleReconcile();
    }

    disconnectedCallback() {
      if (this.qoOnQuantity) document.removeEventListener('quantity-offers:quantity', this.qoOnQuantity);
    }

    /**
     * The quantity that unlocks a gift is not specified by any Liquid file, so
     * both plausible sources are combined and the larger wins:
     *   - the quantity the shopper has picked on the PDP (immediate feedback)
     *   - the quantity of the main product already in the cart (what the schema
     *     paragraph at main-product.liquid:8168 talks about)
     */
    qoEffectiveQuantity() {
      var pdp = this.qoPdpQuantity();
      var cart = QuantityOffersEngine.cartQuantityOf(this.mainProduct);
      return Math.max(pdp, cart);
    }

    qoPdpQuantity() {
      var sectionId = this.sectionId;

      // (a) the checked offer radio inside <quantity-breaks> of this section
      //     (quantity-breaks.liquid:176-187)
      var breaks = document.querySelector('quantity-breaks[data-section="' + sectionId + '"]');
      if (breaks) {
        var radio = breaks.querySelector('.quantity-breaks-container input[type="radio"][name="quantity"]:checked');
        if (radio) return parseInt(radio.value, 10) || 0;
      }

      // (b) the standard quantity input rendered by the same quantity_selector
      //     block when quantity discounts are off
      //     (sections/main-product.liquid:390-405)
      var input = document.getElementById('Quantity-' + sectionId);
      if (input) return parseInt(input.value, 10) || 0;

      return 0;
    }

    evaluate() {
      var quantity = this.qoEffectiveQuantity();

      qoArray(this.querySelectorAll('.quantity-gift')).forEach(function (gift) {
        var threshold = parseInt(gift.getAttribute('data-quantity'), 10);
        if (!isFinite(threshold) || threshold < 1) threshold = 1;

        var unlocked = quantity >= threshold;
        var current = gift.getAttribute('data-unlocked') === 'true';
        if (unlocked === current) return; // idempotency guard — do not replay the animation

        gift.setAttribute('data-unlocked', unlocked ? 'true' : 'false');
        gift.classList.toggle('quantity-gift--unlocked', unlocked);
      });
    }
  };

  /* =======================================================================
   * Registration
   * ===================================================================== */

  if (!customElements.get('quantity-breaks')) customElements.define('quantity-breaks', QuantityBreaks);
  if (!customElements.get('quantity-break-upsells')) customElements.define('quantity-break-upsells', QuantityBreakUpsells);
  if (!customElements.get('quantity-gifts')) customElements.define('quantity-gifts', QuantityGifts);

  /* Start the cart engine once the document is parsed. It no-ops on pages that
     render none of these elements. Shopify.designMode section reloads re-run
     connectedCallback on the new elements, which re-schedules a reconcile. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      qoTry(QuantityOffersEngine.start.bind(QuantityOffersEngine), 'engine start');
    });
  } else {
    qoTry(QuantityOffersEngine.start.bind(QuantityOffersEngine), 'engine start');
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () {
      QuantityOffersEngine.scheduleReconcile();
    });
  }
})();
